import { useCallback, useEffect, useRef, useState } from "react";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";

import type {
  Tag,
  TagCreateRequest,
  TagListResponse,
  TagUpdateRequest,
} from "@/lib/project-types";
import { fetchWithAuth } from "@/lib/utils";
import i18n from "@/lib/i18n";

const PROJECTS_API_BASE = "/api/projects";
const TAGS_API_BASE = "/api/tags";

const jsonHeaders: Record<string, string> = {
  "Content-Type": "application/json",
};

export type TagFetcher = typeof fetchWithAuth;

export interface UseTagsReturn {
  tags: Tag[];
  loading: boolean;
  error: string | null;
  createError: string | null;
  list: () => Promise<Tag[] | null>;
  create: (name: string, color?: string | null) => Promise<Tag | null>;
  update: (tagId: string, updates: TagUpdateRequest) => Promise<Tag | null>;
  archive: (tagId: string) => Promise<boolean>;
  restore: (tagId: string) => Promise<boolean>;
}

function sortTags(tags: Tag[]): Tag[] {
  return [...tags].sort((left, right) => {
    if (left.is_archived !== right.is_archived) return left.is_archived ? 1 : -1;
    return left.name.localeCompare(right.name);
  });
}

/** Manage the inline, project-scoped task-tag lifecycle. */
export function useTags(
  getToken: () => Promise<string>,
  projectId: string | null,
  fetcher: TagFetcher = fetchWithAuth,
): UseTagsReturn {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const projectIdRef = useRef(projectId);
  const fetcherRef = useRef(fetcher);
  const getTokenRef = useRef(getToken);
  fetcherRef.current = fetcher;
  getTokenRef.current = getToken;

  const list = useCallback(async (): Promise<Tag[] | null> => {
    const requestedProjectId = projectIdRef.current;
    if (!requestedProjectId) {
      setTags([]);
      return [];
    }

    setLoading(true);
    setError(null);
    try {
      const res = await fetcherRef.current(
        `${PROJECTS_API_BASE}/${encodeURIComponent(requestedProjectId)}/tags`,
        { headers: {} },
        getTokenRef.current,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);

      const data: TagListResponse = await res.json();
      if (projectIdRef.current === requestedProjectId) {
        setTags(sortTags(data.tags));
      }
      return data.tags;
    } catch (err: unknown) {
      if (projectIdRef.current === requestedProjectId) {
        setError(err instanceof Error ? err.message : i18n.t("projects:tagLoadFailed"));
      }
      return null;
    } finally {
      if (projectIdRef.current === requestedProjectId) setLoading(false);
    }
  }, []);

  const create = useCallback(async (
    name: string,
    color?: string | null,
  ): Promise<Tag | null> => {
    const requestedProjectId = projectIdRef.current;
    const normalizedName = name.trim();
    if (!requestedProjectId || !normalizedName) return null;

    setCreateError(null);
    try {
      const body: TagCreateRequest = {
        name: normalizedName,
        color: color?.trim() || null,
      };
      const res = await fetcherRef.current(
        `${PROJECTS_API_BASE}/${encodeURIComponent(requestedProjectId)}/tags`,
        { method: "POST", headers: jsonHeaders, body: JSON.stringify(body) },
        getTokenRef.current,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);

      const tag: Tag = await res.json();
      if (projectIdRef.current === requestedProjectId) {
        setTags((current) => sortTags([
          ...current.filter((item) => item.id !== tag.id),
          tag,
        ]));
      }
      return tag;
    } catch (err: unknown) {
      if (projectIdRef.current === requestedProjectId) {
        setCreateError(err instanceof Error ? err.message : i18n.t("projects:tagCreateFailed"));
      }
      return null;
    }
  }, []);

  const update = useCallback(async (
    tagId: string,
    updates: TagUpdateRequest,
  ): Promise<Tag | null> => {
    setError(null);
    try {
      const res = await fetcherRef.current(
        `${TAGS_API_BASE}/${encodeURIComponent(tagId)}`,
        { method: "PATCH", headers: jsonHeaders, body: JSON.stringify(updates) },
        getTokenRef.current,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);

      const tag: Tag = await res.json();
      setTags((current) => sortTags([
        ...current.filter((item) => item.id !== tag.id),
        tag,
      ]));
      return tag;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : i18n.t("projects:tagUpdateFailed"));
      return null;
    }
  }, []);

  const archive = useCallback(async (tagId: string): Promise<boolean> => {
    return (await update(tagId, { is_archived: true })) !== null;
  }, [update]);

  const restore = useCallback(async (tagId: string): Promise<boolean> => {
    return (await update(tagId, { is_archived: false })) !== null;
  }, [update]);

  useRefreshOnReconnect(list, Boolean(projectId));

  useEffect(() => {
    projectIdRef.current = projectId;
    setCreateError(null);
    if (!projectId) {
      setTags([]);
      setError(null);
      setLoading(false);
      return;
    }
    void list();
  }, [list, projectId]);

  return { tags, loading, error, createError, list, create, update, archive, restore };
}
